/**
 * PricingLegend — Sprint L
 *
 * Small colour/price legend for the two-tier overview map. Rendered inline on
 * desktop, and as a bottom drawer on mobile (via the `mobileDrawer` prop).
 */
import React, { useState } from "react";
import { Info, X } from "lucide-react";
import { centsToAUD } from "../lib/events";

function LegendRow({ zone }) {
  return (
    <div
      className="flex items-center gap-2 text-xs"
      data-testid={`pricing-zone-row-${zone.id}`}
    >
      <span
        className="inline-block w-4 h-4 rounded-sm border border-black/20 shrink-0"
        style={{ backgroundColor: zone.hex_colour }}
      />
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{zone.name}</div>
        {zone.description && (
          <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] truncate">
            {zone.description}
          </div>
        )}
      </div>
      <span className="font-mono font-bold tabular-nums">
        {zone.base_price_cents === 0 ? "Free" : centsToAUD(zone.base_price_cents)}
      </span>
    </div>
  );
}

/** Desktop-inline legend */
export function PricingLegendInline({ zones = [] }) {
  if (!zones || zones.length === 0) return null;
  return (
    <div
      className="mt-4 border border-[var(--gt-line)] rounded-sm bg-white p-4"
      data-testid="pricing-legend-desktop"
    >
      <div className="gt-label mb-3 flex items-center gap-1.5">
        <Info size={12} /> Pricing zones
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {zones.map((z) => (
          <LegendRow key={z.id} zone={z} />
        ))}
      </div>
    </div>
  );
}

/** Mobile bottom-sheet drawer — button + drawer contents */
export function PricingLegendDrawer({ zones = [] }) {
  const [open, setOpen] = useState(false);
  if (!zones || zones.length === 0) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="pricing-legend-open-btn"
        className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-2 border border-[var(--gt-line)] rounded-sm bg-white hover:border-[var(--gt-ink)]"
      >
        <Info size={12} /> Pricing
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Pricing zones legend"
          data-testid="pricing-legend-drawer"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        >
          <button
            type="button"
            aria-label="Close pricing legend"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/50"
            data-testid="pricing-legend-backdrop"
          />
          <div className="relative w-full sm:max-w-md bg-white rounded-t-lg sm:rounded-lg p-5 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="gt-label flex items-center gap-1.5">
                <Info size={12} /> Pricing zones
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="pricing-legend-close-btn"
                className="w-8 h-8 rounded-sm border border-[var(--gt-line)] flex items-center justify-center hover:bg-[var(--gt-bg-surface)]"
              >
                <X size={14} />
              </button>
            </div>
            <div className="space-y-2">
              {zones.map((z) => (
                <LegendRow key={z.id} zone={z} />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PricingLegendInline;
